import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Tab,
    TextField,
    Typography
} from '@mui/material';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Confirm, Notify, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Loader from '../../../components/loader/Loader';
import { getAllDocumentType } from '../../../data/api/document/DocumentRoutes';
import { updateUser, getAllUsersAvatars, selfDisableAccount } from '../../../data/api/users/UserRoutes';
import useAuth from '../../../hooks/useAuth';
import useIndex from '../../../hooks/useIndex';
import userAvatar from '../../../assets/img/User-Avatar.png';
import styled from 'styled-components';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import validErrors from '../../../hooks/validErrors';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },

    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },

    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },
};

const Configuration = () => {
    const serverUrl = process.env.REACT_APP_BACK_URL;

    const { auth, token, validateUser } = useIndex();
    const { logout } = useAuth();
    const { register, handleSubmit, setValue } = useForm();

    const [submit, setSubmit] = useState(false);
    const [documentType, setDocumentType] = useState(null);
    const [avatarsData, setAvatarsData] = useState([]);
    const [userImage, setUserImage] = useState(auth?.avatar ?? userAvatar);
    const [load, setLoad] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [valueTab, setValueTab] = useState('Anonimo');

    // Si el admin desactivó la cuenta mientras el usuario estaba en sesión,
    // o el propio usuario se desactivó y el token aún no expira, bloqueamos
    // toda edición. La única salida es contactar al admin.
    const isAccountDisabled = Number(auth?.status) === 0;
    const formDisabled = submit || isAccountDisabled;

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleChange = (event, newValue) => {
        setValueTab(newValue);
    };

    useEffect(() => {
        loadData();
        loadAvatars();
    }, []);

    useEffect(() => {
        setUserImage(serverUrl + auth?.avatar);
    }, [auth, serverUrl]);

    const loadAvatars = async () => {
        setLoad(true);

        try {
            const { data } = await getAllUsersAvatars();

            if (data?.success) {
                setAvatarsData(data.data);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(false);
        }
    };

    const loadData = async () => {
        try {
            const { data } = await getAllDocumentType();
            setDocumentType(data.data);
        } catch (error) {
            validErrors(error);
        }
    };

    const handleUserAvatar = (picture) => {
        setValue('avatar', picture.value);
        setUserImage(serverUrl + picture.path);
    };

    const disabledAcount = () => {
        Confirm.prompt(
            'Desactivar cuenta',
            '¿Seguro deseas desactivar tu cuenta?. Al hacerlo no perderás tus registros de juego, pero restringes el uso del juego desde tu cuenta y las jugadas que aún no hayas usado quedarán desactivadas. También evitarás recibir regalos, promociones e información que Fenabocol quiera brindarte. En caso de cambiar de opinión deberás ponerte en contacto con el administrador para activarla nuevamente. <br/><br/>Para desactivar tu cuenta ingresa tu contraseña actual.',
            '',
            'Confirmar',
            'Cancelar',
            (clientAnswer) => disabledAcountConfirm(clientAnswer),
            () => { },
            {
                messageMaxLength: 1923,
                plainText: false,
                width: '400px'
            }
        );
    };

    const disabledAcountConfirm = async (password) => {
        if (!password || password.trim() === '') {
            Notify.failure('Debes ingresar tu contraseña para desactivar la cuenta.');
            return;
        }

        setSubmit(true);
        try {
            const { data } = await selfDisableAccount(token, password);

            if (data?.success) {
                // El backend ya revocó todos los tokens. Avisamos y
                // forzamos el logout local para limpiar el storage y
                // redirigir a home; cualquier request residual caería
                // con 401 y dispararía el flujo de "sesión expirada".
                Report.success(
                    'Cuenta desactivada',
                    data?.message ?? 'Tu cuenta ha sido desactivada.',
                    'Aceptar',
                    () => logout(),
                    { backOverlayColor: 'rgba(0,0,0,0.5)' },
                );
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
        }
    };

    const onSubmit = async (dataForm) => {
        setSubmit(true);

        try {
            const { data } = await updateUser(token, auth.uuid, dataForm);

            if (data.success) {
                validateUser();
                setValue('password', '');
                setValue('password_confirmation', '');
                setShowPassword(false);

                Report.success(
                    'Actualización exitosa',
                    'El usuario ha sido actualizado correctamente.',
                    'Ok'
                );
            } else {
                Notify.failure(data.message);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
        }
    };

    if (!auth?.id) {
        return <Loader />;
    }

    const userInfo = [
        {
            label: 'Nombre',
            value: `${auth?.name ?? ''} ${auth?.lastname ?? ''}`.trim() || '-',
            icon: <PersonOutlineOutlinedIcon />,
        },
        {
            label: 'Correo',
            value: auth?.email ?? '-',
            icon: <BadgeOutlinedIcon />,
        },
        {
            label: 'Teléfono',
            value: auth?.phone ?? '-',
            icon: <BadgeOutlinedIcon />,
        },
        {
            label: auth?.document_type_name ?? 'Documento',
            value: auth?.document ?? '-',
            icon: <BadgeOutlinedIcon />,
        },
        {
            label: 'Jugadas',
            value: auth?.moves ?? 0,
            icon: <SportsEsportsOutlinedIcon />,
        },
        {
            label: 'Rol',
            value: auth?.role ?? '-',
            icon: <ManageAccountsOutlinedIcon />,
        },
        {
            label: 'Estado',
            value: auth?.status_name ?? '-',
            icon: <ManageAccountsOutlinedIcon />,
        },
    ];

    return (
        <PageContainer maxWidth={false}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Header>
                    <Box className="icon">
                        <ManageAccountsOutlinedIcon />
                    </Box>

                    <Box>
                        <Typography className="title">
                            Configuración de cuenta
                        </Typography>

                        <Typography className="subtitle">
                            Actualiza tus datos personales, avatar y contraseña de acceso.
                        </Typography>
                    </Box>
                </Header>

                <Grid container spacing={2}>
                    <Grid item xs={12} lg={4}>
                        <ProfileCard>
                            <UserImageSelect>
                                <Box className="imgUstLat">
                                    <img src={userImage} alt="user-avatar" />
                                </Box>

                                <Box className="descrUser">
                                    <Typography className="role">
                                        {auth?.role ?? 'User'}
                                    </Typography>

                                    <Typography className="name">
                                        {auth?.name ?? 'User Name'}
                                    </Typography>
                                </Box>
                            </UserImageSelect>

                            <StyledDivider>
                                <span>Avatar</span>
                            </StyledDivider>

                            <AvatarSectionTitle>
                                <PhotoCameraOutlinedIcon />
                                <Box>
                                    <Typography className="section-title">
                                        Cambiar avatar
                                    </Typography>
                                </Box>
                            </AvatarSectionTitle>

                            {load ? (
                                <LoadingAvatars>
                                    <CircularProgress size={28} sx={{ color: '#d6aa3a' }} />
                                    <Typography>Cargando avatares...</Typography>
                                </LoadingAvatars>
                            ) : (
                                <AvatarTabs>
                                    <TabContext value={valueTab}>
                                        <Box className="tabs-box">
                                            <TabList
                                                onChange={handleChange}
                                                aria-label="set_avatar"
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                sx={{
                                                    minHeight: 38,
                                                    '& .MuiTab-root': {
                                                        minHeight: 38,
                                                        fontWeight: 900,
                                                        color: 'rgba(15,34,77,0.58)',
                                                        fontSize: '0.78rem',
                                                    },
                                                    '& .Mui-selected': {
                                                        color: '#10285f !important',
                                                    },
                                                    '& .MuiTabs-indicator': {
                                                        backgroundColor: '#d6aa3a',
                                                    },
                                                }}
                                            >
                                                {Object.keys(avatarsData).map((item) => (
                                                    <Tab key={item} label={item} value={item} />
                                                ))}
                                            </TabList>
                                        </Box>

                                        {Object.keys(avatarsData).map((item) => (
                                            <TabPanel key={item} value={item} sx={{ px: 0, py: 1 }}>
                                                <AvatarGrid>
                                                    {avatarsData[item].map((picture) => (
                                                        <ImageSelect
                                                            key={picture.value}
                                                            type="button"
                                                            onClick={() => handleUserAvatar(picture)}
                                                        >
                                                            <img
                                                                src={serverUrl + picture.path}
                                                                alt="user-avatar"
                                                                className="imgUsrLatInt"
                                                            />
                                                        </ImageSelect>
                                                    ))}
                                                </AvatarGrid>
                                            </TabPanel>
                                        ))}
                                    </TabContext>
                                </AvatarTabs>
                            )}

                            <Typography className="credits">
                                Designed by |{' '}
                                <a target="_blank" rel="noreferrer" href="https://www.freepik.com/">
                                    Freepik
                                </a>
                            </Typography>
                        </ProfileCard>
                    </Grid>

                    <Grid item xs={12} lg={8}>
                        <InfoCard>
                            <SectionTitle>
                                <PersonOutlineOutlinedIcon />
                                <Box>
                                    <Typography className="section-title">
                                        Datos de usuario
                                    </Typography>
                                    <Typography className="section-subtitle">
                                        Información actualmente registrada
                                    </Typography>
                                </Box>
                            </SectionTitle>

                            <InfoGrid>
                                {userInfo.map((item) => (
                                    <InfoItem key={item.label}>
                                        <Box className="icon-box">
                                            {item.icon}
                                        </Box>

                                        <Box>
                                            <span>{item.label}</span>
                                            <strong>{item.value}</strong>
                                        </Box>
                                    </InfoItem>
                                ))}
                            </InfoGrid>

                            {isAccountDisabled ? (
                                <DangerAction>
                                    <Box>
                                        <WarningAmberOutlinedIcon />
                                        <Typography>
                                            Tu cuenta está desactivada. Para reactivarla contacta al administrador del sistema.
                                        </Typography>
                                    </Box>
                                </DangerAction>
                            ) : (
                                <DangerAction>
                                    <Box>
                                        <WarningAmberOutlinedIcon />
                                        <Typography>
                                            Puedes desactivar tu cuenta si no deseas seguir usando el sistema.
                                        </Typography>
                                    </Box>

                                    <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={disabledAcount}
                                        disabled={submit}
                                        sx={{
                                            borderRadius: '12px',
                                            fontWeight: 900,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Desactivar cuenta
                                    </Button>
                                </DangerAction>
                            )}
                        </InfoCard>

                        <StyledDivider>
                            <span>Modificar información</span>
                        </StyledDivider>

                        <FormCard>
                            <SectionTitle>
                                <ManageAccountsOutlinedIcon />
                                <Box>
                                    <Typography className="section-title">
                                        Formulario de actualización
                                    </Typography>
                                    <Typography className="section-subtitle">
                                        {isAccountDisabled
                                            ? 'Cuenta desactivada: los campos están bloqueados. Contacta al administrador para reactivarla.'
                                            : 'Cambia tus datos personales y contraseña'}
                                    </Typography>
                                </Box>
                            </SectionTitle>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        defaultValue={auth?.name}
                                        {...register('name')}
                                        label="Nombre"
                                        placeholder="Nombre"
                                        disabled={formDisabled}
                                        sx={fieldStyles}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        defaultValue={auth?.lastname}
                                        {...register('lastname')}
                                        label="Apellido"
                                        placeholder="Apellido"
                                        disabled={formDisabled}
                                        sx={fieldStyles}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        defaultValue={auth?.email}
                                        {...register('email')}
                                        label="Correo"
                                        placeholder="Correo"
                                        disabled={formDisabled}
                                        sx={fieldStyles}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        defaultValue={auth?.phone}
                                        {...register('phone')}
                                        label="Teléfono"
                                        placeholder="Teléfono"
                                        disabled={formDisabled}
                                        sx={fieldStyles}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={8}>
                                            <TextField
                                                required
                                                fullWidth
                                                size="small"
                                                defaultValue={auth?.document}
                                                {...register('document')}
                                                label="Documento"
                                                placeholder="Documento"
                                                disabled={formDisabled}
                                                sx={fieldStyles}
                                            />
                                        </Grid>

                                        <Grid item xs={4}>
                                            <FormControl fullWidth size="small" sx={fieldStyles}>
                                                <InputLabel id="label_doc">Doc.</InputLabel>

                                                <Select
                                                    defaultValue={auth?.document_type_id}
                                                    {...register('document_type_id')}
                                                    labelId="label_doc"
                                                    id="document_type_id"
                                                    label="Doc."
                                                    disabled={formDisabled}
                                                >
                                                    {documentType && documentType.map((item) => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {`${item.short_name} - ${item.name}`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth variant="outlined" size="small" sx={fieldStyles}>
                                        <InputLabel htmlFor="password">Contraseña</InputLabel>

                                        <OutlinedInput
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Contraseña"
                                            label="Contraseña"
                                            {...register('password')}
                                            disabled={formDisabled}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth variant="outlined" size="small" sx={fieldStyles}>
                                        <InputLabel htmlFor="password_confirmation">
                                            Confirmar Contraseña
                                        </InputLabel>

                                        <OutlinedInput
                                            id="password_confirmation"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Confirmar Contraseña"
                                            label="Confirmar Contraseña"
                                            {...register('password_confirmation')}
                                            disabled={formDisabled}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="tog-pass-conf"
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <LoadingButton
                                        fullWidth
                                        loading={submit}
                                        loadingPosition="start"
                                        startIcon={<i className="fa-solid fa-floppy-disk"></i>}
                                        variant="contained"
                                        size="large"
                                        type="submit"
                                        sx={{
                                            borderRadius: '14px',
                                            py: 1.1,
                                            fontWeight: 900,
                                            textTransform: 'uppercase',
                                            background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                            boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                            },
                                        }}
                                    >
                                        <span>{isAccountDisabled ? 'Cuenta desactivada' : 'Guardar cambios'}</span>
                                    </LoadingButton>
                                </Grid>
                            </Grid>
                        </FormCard>
                    </Grid>
                </Grid>
            </form>
        </PageContainer>
    );
};

export default Configuration;

const PageContainer = styled(Container)`
    padding: 1.2rem !important;
    background: #f5f7fb;
    min-height: 100%;

    @media (max-width: 600px) {
        padding: 0.85rem !important;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1rem;

    .icon {
        width: 46px;
        height: 46px;
        border-radius: 15px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.3);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
            font-size: 1.55rem;
        }
    }

    .title {
        color: #10285f;
        font-size: 1.65rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.18rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }
`;

const ProfileCard = styled(Card)`
    padding: 1rem;
    border-radius: 22px !important;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08) !important;
`;

const UserImageSelect = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    .imgUstLat {
        width: 138px;
        height: 138px;
        border: 3px solid rgba(214, 170, 58, 0.72);
        border-radius: 50%;
        background: rgba(16, 40, 95, 0.06);
        overflow: hidden;
        padding: 4px;
        box-shadow: 0 10px 22px rgba(15, 34, 77, 0.12);

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 50%;
        }
    }

    .descrUser {
        text-align: center;
        margin-top: 0.8rem;

        .role {
            color: #10285f;
            font-size: 1rem;
            font-weight: 900;
            text-transform: uppercase;
        }

        .name {
            color: rgba(15, 34, 77, 0.62);
            font-size: 0.9rem;
            font-weight: 700;
            margin-top: 0.15rem;
        }
    }
`;

const StyledDivider = styled.div`
    margin: 1rem 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 34, 77, 0.13);
    }

    span {
        padding: 0.32rem 0.85rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        font-size: 0.76rem;
    }
`;

const AvatarSectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.75rem;

    svg {
        color: #d6aa3a;
        font-size: 1.25rem;
        flex-shrink: 0;
    }

    .section-title {
        color: #10285f;
        font-size: 0.95rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .section-subtitle {
        margin-top: 0.1rem;
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.76rem;
        font-weight: 600;
    }
`;

const LoadingAvatars = styled.div`
    min-height: 120px;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const AvatarTabs = styled.div`
    .tabs-box {
        border-bottom: 1px solid rgba(15, 34, 77, 0.1);
    }
`;

const AvatarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(58px, 1fr));
    gap: 0.55rem;
`;

const ImageSelect = styled.button`
    width: 100%;
    aspect-ratio: 1/1;
    border: 2px solid rgba(15, 34, 77, 0.1);
    border-radius: 50%;
    background: rgba(16, 40, 95, 0.04);
    overflow: hidden;
    padding: 2px;
    transition: 0.2s ease;
    cursor: pointer;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
    }

    &:hover {
        border-color: #d6aa3a;
        transform: translateY(-2px);
        box-shadow: 0 8px 18px rgba(15, 34, 77, 0.12);
    }
`;

const InfoCard = styled.div`
    padding: 1rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);
`;

const FormCard = styled.div`
    padding: 1rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    svg {
        color: #d6aa3a;
        font-size: 1.4rem;
        flex-shrink: 0;
    }

    .section-title {
        color: #10285f;
        font-size: 1rem;
        font-weight: 900;
        line-height: 1.2;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.8rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const InfoItem = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: center;
    padding: 0.75rem;
    border-radius: 16px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    min-width: 0;

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        flex-shrink: 0;

        svg {
            font-size: 1.15rem;
        }
    }

    span {
        display: block;
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.72rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.35px;
    }

    strong {
        display: block;
        margin-top: 0.1rem;
        color: #10285f;
        font-size: 0.9rem;
        font-weight: 900;
        word-break: break-word;
    }
`;

const DangerAction = styled.div`
    margin-top: 1rem;
    padding: 0.8rem;
    border-radius: 16px;
    background: rgba(211, 47, 47, 0.06);
    border: 1px solid rgba(211, 47, 47, 0.14);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    > div {
        display: flex;
        align-items: center;
        gap: 0.6rem;
    }

    svg {
        color: #d32f2f;
        flex-shrink: 0;
    }

    p {
        color: rgba(15, 34, 77, 0.7);
        font-size: 0.84rem;
        font-weight: 700;
    }

    @media (max-width: 700px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const credits = styled(Typography)``;